import React, { useState } from "react"
import { useRef } from "react"
import { observer } from "mobx-react-lite"
import { Field, Formik } from "formik"
import { Appearance, TextInput } from "react-native"
import { FormProps } from "./form"
import * as yup from "yup"
import { translate } from "../../i18n"
import { TextField } from "../field/text-field/text-field"
import { presetsColors } from "../text/text.presets"

export interface LoginFormProps extends FormProps {
  initialValues?: {
    username?: string,
    password?: string
  }
}

/**
 * Describe your component here
 */
export const LoginForm = observer(function LoginForm(props: LoginFormProps) {
  const { initialValues = {}, onSubmit, colorScheme = "light" || Appearance.getColorScheme(), innerRef, ...rest } = props
  const [ showHelper, setShowHelper ] = useState<boolean>(false)

  const input = {
    username: useRef<TextInput>(),
    password: useRef<TextInput>()
  }
  const nextInput = {
    username: () => input.password.current.focus()
  }
 
  const validationSchema = yup.object({
    username: yup.string().emailOrPhone().nullable().required().label(translate("loginForm.username")),
    password: yup.string().ascii().min(6).nullable().required().label(translate("loginForm.password"))
  })

  return (
    <Formik
      enableReinitialize
      initialValues={{
        username: null,
        password: null,
        ...initialValues
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ isValid, handleChange, handleSubmit, handleBlur, errors, setErrors, touched, values }) => (
        <>
          <TextField
            FieldComponent={Field}
            name="username"
            txPrefix="loginForm"
            colorScheme={colorScheme}
            input={input}
            nextInput={nextInput}
            formControlProps={{
              ...showHelper
              && ((!values && !values.username) || !(errors && errors.username && touched && touched.username)) ? {
                helperTextTx: 'loginForm.phoneExample',
                helperTextProps: {
                  ...presetsColors(colorScheme)
                }
              } : {},
              isRequired: true,
              labelPreset: "title3",
              inputProps: {
                autoComplete: "email",
                textContentType: "emailAddress",
                keyboardType: "email-address",
                onChangeText: (text) => {
                  handleChange("username")((text || '').trim())
                },
                onFocus: (event) => {
                  if ((event.nativeEvent && event.nativeEvent.text ? event.nativeEvent.text : '').length === 0) {
                    setErrors({ ...errors, username: undefined })
                    setShowHelper(true)
                  }
                },
                onBlur: (event) => {
                  handleBlur && handleBlur("username")(event)
                  setShowHelper(false)
                }
              }
            }}
          />
          <TextField
            name="password"
            txPrefix="loginForm"
            colorScheme={colorScheme}
            input={input}
            nextInput={nextInput}
            formControlProps={{
              isRequired: true,
              labelPreset: "title3",
              inputProps: {
                type: "password",
                autoComplete: "password",
                textContentType: "password",
                onSubmitEditing: () => (isValid && handleSubmit()),
                keyboardType: "default"
              }
            }}
          />
        </>
      )}
    </Formik>
  )
})

LoginForm.displayName = 'LoginForm'
